// Ripple-insert placement for dropping a clip into a shared row. Same-row
// clips never overlap (they share one canvas layer): the clip lands where the
// (already snapped) pointer put it, clamped after the members it follows, and
// any member it would collide with shifts just far enough to clear it. That
// displacement can cascade, but an existing gap absorbs it: members after a
// large-enough gap keep their original position.
//
// members: [{ id, startFrame, endFrame }] — the target row WITHOUT the
// dragged clip itself. Returns the final ghost/drop position and the shifts
// to apply on commit ([] when the clip fits without pushing anyone).
export function resolveRowInsertion({ desiredStart, clipLen, members = [] }) {
  const len = Math.max(1, Math.round(clipLen));
  const start0 = Math.max(0, Math.round(desiredStart));
  const sorted = [...members].sort(
    (a, b) => a.startFrame - b.startFrame || a.endFrame - b.endFrame
  );

  // Insert after every member whose center sits at/before the candidate START
  // (the dragged clip's left edge, i.e. where the user is pointing). Comparing
  // centers instead would make it impossible to place a long clip before a
  // short left-anchored member — its center could never reach far enough left.
  let insertIndex = 0;
  sorted.forEach((m) => {
    if ((m.startFrame + m.endFrame) / 2 <= start0) insertIndex += 1;
  });

  let prevEnd = 0;
  for (let i = 0; i < insertIndex; i++) prevEnd = Math.max(prevEnd, sorted[i].endFrame);
  const startFrame = Math.max(start0, prevEnd);
  const endFrame = startFrame + len;

  const shifts = [];
  let occupiedUntil = endFrame;
  for (let i = insertIndex; i < sorted.length; i++) {
    const member = sorted[i];
    if (member.startFrame >= occupiedUntil) break;

    const delta = occupiedUntil - member.startFrame;
    shifts.push({ id: member.id, delta });
    occupiedUntil = member.endFrame + delta;
  }
  return { startFrame, endFrame, shifts };
}

// Push the clips reached by an extended right edge. Existing gaps absorb the
// displacement, so propagation stops as soon as there is enough free space.
export function resolveRightResizeRipple({ endFrame, followers = [] }) {
  const shifts = [];
  let occupiedUntil = Math.round(endFrame);
  const sorted = [...followers].sort(
    (a, b) => a.startFrame - b.startFrame || a.endFrame - b.endFrame
  );
  for (const member of sorted) {
    if (member.startFrame >= occupiedUntil) break;
    const delta = occupiedUntil - member.startFrame;
    shifts.push({ id: member.id, delta });
    occupiedUntil = member.endFrame + delta;
  }
  return shifts;
}
